import { ProviderDataType } from "@/api/ProviderApi";
import { detectIdentificationType, normalizeIdentification } from "@utils/Identification";
import { normalizePhoneNumber } from "@utils/Phone";
import { ProviderImportField, ProviderImportValueKind, findProviderImportField } from "./providerImportFields";
import { clean, parseCashCreditType, parseImportInteger } from "./core/values";
import { ColumnMapping as GenericColumnMapping } from "./core/types";

export { parseImportInteger };

/** Codigo de pais que se asume cuando el archivo no trae la columna (Costa Rica). */
export const DEFAULT_PHONE_COUNTRY_CODE = "506";

/** Columna del archivo -> atributo del proveedor. `null` = columna sin mapear. */
export type ColumnMapping = GenericColumnMapping & Record<number, ProviderImportField["name"] | null>;

/** "Contado" -> 0, "Credito" -> 1. Devuelve `undefined` si el valor no se reconoce. */
export const parseProviderType = parseCashCreditType;

/** Aplica la normalizacion que corresponde al tipo del atributo destino. */
export const normalizeImportValue = (kind: ProviderImportValueKind, raw: unknown): unknown => {
    switch (kind) {
        case "identification":
            return normalizeIdentification(`${raw ?? ""}`);
        case "phone":
        case "phoneCountryCode":
            return normalizePhoneNumber(`${raw ?? ""}`);
        case "email":
            return clean(raw).toLowerCase();
        case "integer":
            return parseImportInteger(raw);
        case "providerType":
            return parseProviderType(raw);
        case "code":
        case "text":
        default:
            return clean(raw);
    }
};

/**
 * Aplica el mapeo de columnas a una fila cruda y devuelve los atributos del proveedor.
 *
 * `lookupName` viaja vacio a proposito: `Provider` no tiene ninguna entidad relacionada
 * que haya que crear por nombre (no hay zona ni categoria), a diferencia de clientes y
 * productos. El motor generico lo sigue pidiendo, asi que se devuelve la cadena vacia.
 */
export const mapRowToProvider = (
    row: string[],
    mapping: ColumnMapping
): { provider: ProviderDataType; lookupName: string } => {
    const provider: Record<string, unknown> = {};

    Object.entries(mapping).forEach(([columnIndex, fieldName]) => {
        if (!fieldName) return;

        const field = findProviderImportField(fieldName);
        if (!field) return;

        const value = normalizeImportValue(field.kind, row[Number(columnIndex)]);

        // Un valor vacio no debe pisar el default del modelo.
        if (value === undefined || value === "") return;

        provider[fieldName] = value;
    });

    // El archivo no trae el tipo de identificacion: se deduce del formato del numero.
    const tipoIdentificacion = detectIdentificationType(`${provider.identificacion ?? ""}`);
    if (tipoIdentificacion) {
        provider.tipo_identificacion = tipoIdentificacion;
    }

    // Sin columna de codigo de pais se asume Costa Rica, que es el formato que ya usa
    // el resto del sistema al armar el XML de Hacienda.
    if (!provider.telefono1_cd) provider.telefono1_cd = DEFAULT_PHONE_COUNTRY_CODE;
    if (!provider.telefono2_cd) provider.telefono2_cd = DEFAULT_PHONE_COUNTRY_CODE;

    return { provider: provider as ProviderDataType, lookupName: "" };
};
